import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  Tooltip,
  useModal,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { AttributesStep } from './attributes-step';
import { ObjectStep } from './object-step';
import { PeopleStep } from './people-step';
import { ReviewStep } from './review-step';
import type { NavStep } from './steps';
import * as styles from './add-relationships-modal.css';
import {
  useAddRelationshipsWizard,
  type AddRelationshipsModalData,
  type AddRelationshipsWizard,
} from './use-add-relationships-wizard';

export type { AddRelationshipsModalData };

type StepTabProps = { step: NavStep; index: number };

function StepTab({ step, index }: StepTabProps) {
  return (
    <Tooltip isCustom content={step.disabledMessage} isDisabled={!step.isDisabled}>
      {(context) => (
        <TabsTab
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          value={step.value}
          isDisabled={step.isDisabled}
          icon="check-circle"
          iconColor={step.isComplete ? 'success' : 'subtle'}
          data-test-add-relationships-nav-step={index}
        >
          {index + 1}. {step.title}
        </TabsTab>
      )}
    </Tooltip>
  );
}

type WizardPanelsProps = { wizard: AddRelationshipsWizard; data: AddRelationshipsModalData };

function WizardPanels({ wizard, data }: WizardPanelsProps) {
  return (
    <>
      <TabsPanel value="people">
        <PeopleStep
          picker={wizard.people}
          isLoading={wizard.isLoading}
          canSelect={wizard.canSelect}
          canIdentifyByPhone={wizard.canIdentifyByPhone}
          peoplePlural={wizard.peoplePlural}
        />
      </TabsPanel>

      {data.preselectedObject == null ? (
        <TabsPanel value="objects">
          <ObjectStep
            picker={wizard.objects}
            objectType={data.objectType}
            workspaceId={data.workspaceId}
            isLoading={wizard.isLoading}
            canSelect={wizard.canSelect}
          />
        </TabsPanel>
      ) : null}

      <TabsPanel value="attributes">
        <AttributesStep editor={wizard.editor} isSaving={wizard.isSubmitting} />
      </TabsPanel>

      <TabsPanel value="review">
        <ReviewStep
          people={wizard.people.selected}
          objects={wizard.objects.selected}
          objectType={data.objectType}
          attributeCount={Object.keys(wizard.editor.getModelAttributes().attrs).length}
          peopleSingular={wizard.peopleSingular}
        />
      </TabsPanel>
    </>
  );
}

type WizardFooterProps = { wizard: AddRelationshipsWizard; onClose: () => void };

function WizardFooter({ wizard, onClose }: WizardFooterProps) {
  const isFirstStep = wizard.previousStep == null;

  return (
    <ModalFooter>
      <Button
        variant="secondary"
        data-test-add-relationships-prev-btn
        onClick={() => (isFirstStep ? onClose() : wizard.setStep(wizard.previousStep))}
      >
        {isFirstStep ? 'Cancel' : 'Back'}
      </Button>
      <Button
        variant="primary"
        data-test-add-relationships-next-btn
        isDisabled={wizard.isAdvanceDisabled}
        isLoading={wizard.isSubmitting}
        onClick={wizard.advance}
      >
        {wizard.step === 'review' ? 'Add relationships' : 'Next'}
      </Button>
    </ModalFooter>
  );
}

export function AddRelationshipsModal({
  data,
  modalState,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<AddRelationshipsModalData>) {
  const wizard = useAddRelationshipsWizard({ data, onConfirm });

  return (
    <Modal
      state={modalState}
      title="Add relationships"
      className={styles.modal}
      isDismissable={!wizard.isSubmitting}
      data-test-add-relationships-modal
    >
      <ModalBody>
        <Tabs activeTab={wizard.step} onChange={wizard.setStep} tabsListAlignInline="space-evenly">
          <TabsList mt="200">
            {wizard.steps.map((step, index) => (
              <StepTab key={step.value} step={step} index={index} />
            ))}
          </TabsList>
          <WizardPanels wizard={wizard} data={data} />
        </Tabs>
      </ModalBody>
      <WizardFooter wizard={wizard} onClose={onClose} />
    </Modal>
  );
}

export function useAddRelationshipsModal() {
  return useModal(AddRelationshipsModal);
}
